import Modal from '../Modal';
import styles from './FileUploadModal.module.scss';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { loader } from 'graphql.macro';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { CF_DOMAIN } from '../../../constants/aws';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useCurrentUser } from '../../../providers/UserProvider';
import { Text } from '../../typography';
import { MAX_FILE_UPLOAD_SIZE, toastVariant } from '../../../constants/misc';
import { Form } from 'react-bootstrap';
import { DropdownText } from '../../dropdowns/DropdownText';
import {
  fileStatusTypes,
  fileDescriptionTypes,
} from '../../../../src/utilities/files';
import DropdownCombo from '../../dropdowns/DropdownCombo';
import UserSelector from '../../UserSelector';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import FileUploadInput from '../../files/FileUploadInput';
import { dropdownTypes } from '../../dropdowns/dropdown';
import VehicleCardList from '../../vehicles/VehicleCardList';
import { useEffect, useState } from 'react';
import { useFileUpload } from '../../../hooks/offline-hooks/addFile';
import Select from 'react-select';
import { showToast } from '../../../graphql/cache/modal';

const uploadUrlQuery = loader('../../../graphql/queries/s3.graphql');
const fetchRequirementsForFile = loader(
  'src/pages/user/Requirements.requirements.graphql',
);
const requirementsQuery = loader(
  'src/pages/user/Requirements.requirements.graphql',
);
const updateIncidentImageMutation = loader(
  'src/pages/incidents/IncidentImageCard.edit.graphql',
);

export default function FileUploadModal() {
  const { workspaceId, workspaceVehicles, allVehicles } = useWorkspace();
  const { user: currentUser, isAdmin } = useCurrentUser();
  const client = useApolloClient();
  const { modalState, closeModal, updateModal } = useModal();
  const { t } = useTranslation();
  const {
    modalTitle,
    show,
    status,
    type,
    asset,
    expires,
    user,
    downloadAllowed,
    onLoading,
    name,
    size,
    fileToUpload,
    simple,
    allWorkspaces,
    onSubmit,
    assessment,
    previousAdditional,
    accept,
    from,
    fileToEdit = null,
    description,
    requirement = null,
    hidden = false,
    onClose,
    isCertification,
    riskRegistryId,
    announcementId,
  } = modalState.fileUploadModal;

  const { addUploadFileResults } = useInspection();
  const onHide = () => {
    setSelectedRequirement(null);
    closeModal({ modalName: modals.fileUpload });
    onClose();
  };
  const [updateIncidentImage] = useMutation(updateIncidentImageMutation);
  const { updateFile } = useFileUpload();
  const update = (variables) =>
    updateModal({ modalName: modals.fileUpload, variables: { ...variables } });

  const { data: { users = [{}] } = {} } = useQuery(fetchRequirementsForFile, {
    skip: !user?.id,
    variables: { id: `${user?.id}` },
  });

  const { data: { requirements = [] } = {} } = useQuery(requirementsQuery);

  const fileUser = users[0];

  const missingFileRequirements = requirements?.filter(
    (requirement) =>
      !(requirement?.files || []).some((file) => file.user?.id === fileUser.id),
  );
  const userRequirementOptions = missingFileRequirements?.map((m) => {
    return { value: m.id, label: m.title } || [];
  });
  const handleAssessmentFile = (file) => {
    onLoading(file.size);
    addUploadFileResults(onSubmit, file, name, previousAdditional);
  };
  const handleUpload = (
    file,
    {
      user,
      asset,
      expiry,
      name,
      status,
      type,
      workspaceId,
      downloadAllowed,
      description,
      riskRegistryId,
      announcementId,
    },
  ) => {
    showToast({ variant: toastVariant.info, title: 'Uploading File...' });
    onLoading(file.size);
    const fileUUID = uuidv4();
    const fileParts = file.name.split('.');
    const fileName = fileUUID;
    const fileType = fileParts[fileParts.length - 1];
    client
      .query({
        query: uploadUrlQuery,
        variables: {
          fileName: `assets/${currentUser?.company?.id}/${fileName}.${fileType}`,
          fileType,
        },
      })
      .then((data) => {
        const signedUrl = data.data.simpleStorageUploadUrl;
        const options = {
          headers: {
            'Content-Type': fileType === 'pdf' ? 'application/pdf' : fileType,
          },
        };
        axios.put(signedUrl, file, options).then((result) => {
          if (result.status === 200) {
            onSubmit({
              url: `${CF_DOMAIN(currentUser)}${fileName}.${fileType}`,
              size: file.size,
              extension: fileType,
              fileType: file.type.length ? file.type : fileType,
              name: name || file.name,
              userId: user?.id,
              vehicleId: asset?.id,
              expires: expiry,
              status,
              type,
              workspaceId,
              downloadAllowed,
              description,
              requirementId: requirement?.id || requirement?.value || null,
              hidden,
              isCertification: isCertification || !!requirement?.id,
              riskRegistryId,
              announcementId,
            });
          }
        });
      });
  };

  useEffect(() => {
    if (!!fileToEdit?.id) {
      update({
        ...fileToEdit,
        asset: fileToEdit?.vehicle,
        expires: fileToEdit?.expires,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileToEdit]);

  const [selectedRequirement, setSelectedRequirement] = useState({
    value: requirement?.id,
    label: requirement?.title,
    ...requirement,
  });

  useEffect(() => {
    if (selectedRequirement?.id !== requirement?.id) {
      setSelectedRequirement({
        value: requirement?.id,
        label: requirement?.title,
        ...requirement,
      });
    }
  }, [requirement, selectedRequirement?.id]);

  const isCert =
    isCertification ||
    fileToEdit?.isCertification ||
    !!requirement?.id ||
    !!selectedRequirement?.id;

  return (
    <Modal
      disableClickOutside
      open={show || false}
      onClose={onHide}
      title={modalTitle ? modalTitle : t(keys.files.UPLOAD_FILE)}
      submitText={!!fileToEdit ? t(keys.action.SAVE) : t(keys.action.UPLOAD)}
      submitButtonIcon={'cloud_upload'}
      submitDisabled={(!!size && size > MAX_FILE_UPLOAD_SIZE) || !name}
      onSubmit={() => {
        const file = modalState.fileUploadModal;
        if (!!fileToEdit) {
          if (fileToEdit?.__typename === 'IncidentImage') {
            updateIncidentImage({
              variables: {
                id: file.id,
                description,
              },
            });
          } else {
            updateFile({
              file,
              hidden,
              requirementId: requirement?.value || null,
              expires,
              userId: user?.id || null,
              expiry: expires || null,
              assetId: asset?.id || null,
              name,
              status,
              type,
              downloadAllowed,
              description,
            });
          }
        } else {
          assessment
            ? handleAssessmentFile(fileToUpload)
            : handleUpload(fileToUpload, {
                user,
                expiry: expires,
                asset,
                name,
                status,
                type,
                workspaceId: allWorkspaces ? null : workspaceId,
                downloadAllowed,
                description,
                hidden,
                requirementId: requirement?.value || null,
                riskRegistryId,
                announcementId,
              });
        }
      }}
    >
      {!fileToEdit && (
        <div className={styles.input}>
          <FileUploadInput
            accept={accept}
            onChange={(e) => {
              update({
                fileToUpload: e.target.files[0],
                name: e.target.files[0].name,
                size: e.target.files[0].size,
                asset,
                user,
                requirement,
              });
            }}
          />
          {size > MAX_FILE_UPLOAD_SIZE && (
            <Text color="red" weight="semibold">
              {t(keys.files.FILE_SIZE_WARNING, { size: '100MB' })}
            </Text>
          )}
        </div>
      )}
      {(!!size || !!fileToEdit?.id) && (
        <div className={styles.content}>
          {from === 'ASSET' && (
            <>
              <Text noMargin weight="semiBold">
                {t(keys.common.ASSET)}
              </Text>
              <VehicleCardList vehicle={asset} readOnly={true} />
            </>
          )}
          <div className={styles.field}>
            <Text className={styles.label} noMargin weight="semiBold">
              {`${t(keys.common.TITLE)}`}
              <span className={styles.red}>*</span>
            </Text>
            <Form.Control
              type="text"
              placeholder={t(keys.files.TITLE_PLACEHOLDER)}
              value={
                name && name?.includes('.') ? name.split('.')[0] : name || ''
              }
              onChange={(e) =>
                update({
                  name:
                    e.target.value.charAt(0).toUpperCase() +
                    e.target.value.slice(1),
                })
              }
            />
          </div>
          <div className={styles.field}>
            <Text className={styles.label} noMargin weight="semiBold">
              {`${t(keys.common.DESCRIPTION)}`}
            </Text>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder={t(keys.common.IMAGE_DESCRIPTION_PLACEHOLDER)}
              value={description}
              onChange={(e) =>
                update({
                  description: e.target.value,
                })
              }
            />
          </div>
          <div className={styles.field}>
            <Text className={styles.label} noMargin weight="semiBold">
              {t(keys.action.EXPIRES)}
            </Text>
            <input
              className={styles.dateSelector}
              type="date"
              onChange={(e) => {
                update({ expires: e.target.value });
              }}
              value={expires}
            />
          </div>
          {!simple && (
            <div>
              <div className={styles.field}>
                <Text className={styles.label} noMargin weight="semiBold">
                  {t(keys.common.STATUS)}
                </Text>
                <DropdownText
                  selected={status}
                  onChange={(selected) => update({ status: selected })}
                  onRemove={() => update({ status: null })}
                  items={fileStatusTypes.sort()}
                  namespace="files"
                  placeholder={t(keys.common.STATUS)}
                  noMargin
                />
              </div>
              <div className={styles.field}>
                <Text className={styles.label} noMargin weight="semiBold">
                  {t(keys.common.TYPE)}
                </Text>
                <DropdownText
                  selected={type}
                  onChange={(selected) => update({ type: selected })}
                  onRemove={() => update({ type: null })}
                  items={fileDescriptionTypes.sort()}
                  fixedDropdownSize={true}
                  namespace="files"
                  placeholder={t(keys.common.TYPE)}
                  noMargin
                />
              </div>
              {from !== 'ASSET' && (
                <>
                  <div className={styles.field}>
                    <Text className={styles.label} noMargin weight="semiBold">
                      {t(keys.common.ASSET)}
                    </Text>
                    <DropdownCombo
                      items={
                        isAdmin || allWorkspaces
                          ? allVehicles
                          : workspaceVehicles
                      }
                      type={dropdownTypes.ASSET}
                      onChange={(selected) => {
                        update({ asset: selected });
                      }}
                      selected={asset}
                      noMargin
                    />
                  </div>
                  <div className={styles.field}>
                    <Text className={styles.label} noMargin weight="semiBold">
                      {t(keys.common.USER)}
                    </Text>
                    <UserSelector
                      cardProps={{ mediumAvatar: true }}
                      disableRemove={from === 'USER'}
                      className={styles.avatarBox}
                      type="user"
                      userQuery={allWorkspaces ? 'all' : 'workspace'}
                      selected={user}
                      onClose={() => {
                        update({ user: null });
                      }}
                      onSelectionChanged={(user) => {
                        update({ user });
                      }}
                    />
                  </div>
                </>
              )}
              {!!isCert && from !== 'ASSET' && (
                <div className={styles.field}>
                  <Text className={styles.label} noMargin weight="semiBold">
                    Certification
                  </Text>
                  <Select
                    options={userRequirementOptions || []}
                    placeholder="Select Requirement"
                    value={selectedRequirement}
                    onChange={(value) => {
                      setSelectedRequirement(value);
                      update({ requirement: value });
                    }}
                  />
                </div>
              )}

              <Form.Check
                inline
                className={styles.check}
                checked={downloadAllowed}
                onChange={(e) => {
                  update({ downloadAllowed: e.target.checked });
                }}
                label={t(keys.files.ALLOW_DOWNLOAD)}
                type="checkbox"
                value={downloadAllowed}
              />
              <Form.Check
                inline
                className={styles.check}
                checked={hidden}
                onChange={(e) => {
                  update({ hidden: e.target.checked });
                }}
                label={t(keys.common.PRIVATE)}
                type="checkbox"
                value={hidden}
              />
            </div>
          )}
          <br />
          <Text
            className={styles.disclaimer}
            size="xs"
            color="secondary"
            weight="semiBold"
            textAlign="center"
          >
            {t(keys.files.FILE_COMPLIANCE_WARNING)}
          </Text>
        </div>
      )}
    </Modal>
  );
}
